import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";

const values = [
  { icon: "ri-shield-check-line", title: "Integrity", desc: "We operate with full transparency, ethical standards, and accountability to every client and partner." },
  { icon: "ri-global-line", title: "Global Reach", desc: "Our platform bridges continents, connecting diaspora communities to African markets and global capital." },
  { icon: "ri-book-open-line", title: "Education First", desc: "We believe knowledge is the foundation of lasting wealth. Every service we offer is rooted in financial literacy." },
  { icon: "ri-team-line", title: "Community", desc: "Harambee means pulling together. Collective advancement through shared purpose drives everything we do." },
  { icon: "ri-seedling-line", title: "Long-Term Thinking", desc: "We focus on intergenerational wealth — not quick wins. Our strategies are built for decades, not quarters." },
  { icon: "ri-links-line", title: "Partnership", desc: "We grow alongside our clients and partners, building relationships grounded in trust and mutual success." },
];

const timeline = [
  { year: "2019", title: "Foundation", desc: "7 Hills Development Group LLC established its broader vision of serving underrepresented investor communities across the African Diaspora." },
  { year: "2021", title: "Harambee Concept Development", desc: "Research, relationship-building, and strategic planning shaped the Harambee Global Ventures concept as a diaspora-centered platform for education, advisory, and cross-border opportunity." },
  { year: "2023", title: "Nairobi Hub Planning", desc: "Nairobi emerged as the intended operating anchor for a future training center, relationship network, and regional advisory footprint." },
  { year: "2024", title: "Trading Academy Launch Plan", desc: "The business plan outlines the launch of the first Harambee Trading Academy program in Nairobi, with both online and in-person futures trading education." },
  { year: "2025", title: "Digital Platform Expansion Plan", desc: "The roadmap calls for expanded webinars, workshops, and digital learning resources to support investor readiness across the global diaspora." },
  { year: "2026", title: "Advisory & Capital Solutions Roadmap", desc: "The next phase of the plan anticipates broader advisory services, strategic partnerships, and qualified pathways into family office, private credit, and co-investment support." },
];

export default function AboutPage() {
  return (
    <div className="font-['Inter']">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[60vh] min-h-[480px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/assets/images/site/about001-1920x800.jpg"
            alt="About Harambee Global Ventures"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#0D1F14]/75" />
        </div>
        <div className="relative z-10 text-center px-6">
          <span className="inline-block text-[#C9A961] text-xs font-semibold uppercase tracking-widest mb-4">Our Story</span>
          <h1 className="font-['Cormorant_Garamond'] text-5xl md:text-6xl font-bold text-white mb-4">About Harambee</h1>
          <p className="text-white/65 text-lg max-w-xl mx-auto font-light">Pulling together to build a legacy of wealth, knowledge, and opportunity across the African Diaspora.</p>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-[1280px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="inline-block text-[#C9A961] text-xs font-semibold uppercase tracking-widest mb-4">Who We Are</span>
            <h2 className="font-['Cormorant_Garamond'] text-4xl font-bold text-[#2B2D2F] mb-6">A Platform Built for the Diaspora, by People Who Understand It</h2>
            <div className="space-y-4 text-[#6B6B6B] text-base leading-relaxed">
              <p>Harambee Global Ventures is an international subsidiary of 7 Hills Development Group LLC, founded with a singular purpose: to close the wealth gap facing the African Diaspora through disciplined financial education, strategic advisory, and cross-border investment access.</p>
              <p>We recognized that millions of diaspora professionals and families were earning well but lacking the structured pathways, trusted advisors, and investment networks needed to build lasting, intergenerational wealth. Harambee was built to change that.</p>
              <p>Anchored in Nairobi and connected through Fort Lauderdale, Florida, we are building a cross-border platform that links East African opportunity with diaspora communities, strategic partners, and global ambition.</p>
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/services" className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#1A4D3C] text-white text-sm font-medium hover:bg-[#0D2E1E] transition-colors whitespace-nowrap cursor-pointer">
                Our Services <i className="ri-arrow-right-line" />
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-[#1A4D3C] text-[#1A4D3C] text-sm font-medium hover:bg-[#1A4D3C] hover:text-white transition-colors whitespace-nowrap cursor-pointer">
                Get in Touch
              </Link>
            </div>
          </div>
          <div className="relative">
            <img
              src="/assets/images/site/about002-700x600.jpg"
              alt="Harambee leadership"
              className="w-full h-[500px] object-cover object-top rounded-lg"
            />
            <div className="absolute -bottom-6 -left-6 bg-[#C9A961] rounded-lg p-6">
              <div className="font-['Cormorant_Garamond'] text-4xl font-bold text-[#2B2D2F]">7+</div>
              <div className="text-[#2B2D2F] text-xs font-semibold uppercase tracking-wider mt-1">Years of Impact</div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 px-6 bg-[#F8F6F3]">
        <div className="max-w-[1280px] mx-auto">
          <div className="text-center mb-14">
            <span className="inline-block text-[#C9A961] text-xs font-semibold uppercase tracking-widest mb-3">What Guides Us</span>
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-bold text-[#2B2D2F]">Our Core Values</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((v) => (
              <div key={v.title} className="bg-white rounded-lg p-8">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A4D3C]/10 mb-5">
                  <i className={`${v.icon} text-[#1A4D3C] text-xl`} />
                </div>
                <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#2B2D2F] mb-3">{v.title}</h3>
                <p className="text-[#6B6B6B] text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-[900px] mx-auto">
          <div className="text-center mb-14">
            <span className="inline-block text-[#C9A961] text-xs font-semibold uppercase tracking-widest mb-3">Business Plan Roadmap</span>
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-bold text-[#2B2D2F]">Milestones</h2>
          </div>
          <div className="relative">
            <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px bg-[#C9A961]/30 hidden md:block" />
            <div className="space-y-12">
              {timeline.map((item, i) => (
                <div key={item.year} className={`flex flex-col md:flex-row gap-6 md:gap-12 items-start md:items-center ${i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"}`}>
                  <div className={`flex-1 ${i % 2 === 0 ? "md:text-right" : "md:text-left"}`}>
                    <div className="font-['Cormorant_Garamond'] text-4xl font-bold text-[#C9A961] mb-2">{item.year}</div>
                    <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#2B2D2F] mb-2">{item.title}</h3>
                    <p className="text-[#6B6B6B] text-sm leading-relaxed">{item.desc}</p>
                  </div>
                  <div className="hidden md:flex w-5 h-5 rounded-full bg-[#C9A961] border-4 border-white flex-shrink-0 z-10" />
                  <div className="flex-1 hidden md:block" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-[#1A4D3C]">
        <div className="max-w-[1280px] mx-auto text-center">
          <h2 className="font-['Cormorant_Garamond'] text-4xl font-bold text-white mb-4">Ready to Be Part of the Story?</h2>
          <p className="text-white/65 text-lg mb-8 font-light max-w-xl mx-auto">Join a growing community of diaspora investors and professionals building wealth with purpose and discipline.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/trading-academy" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-md bg-[#C9A961] text-[#2B2D2F] font-semibold text-sm hover:bg-[#b8943f] transition-colors whitespace-nowrap cursor-pointer">
              Apply for the Trading Academy <i className="ri-arrow-right-line" />
            </Link>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-md border border-white/40 text-white font-medium text-sm hover:border-[#C9A961] hover:text-[#C9A961] transition-colors whitespace-nowrap cursor-pointer">
              Book a Consultation
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
